// Cockpit start/stop for a whole-session stroke sensor log (same file format as
// the 60 s Settings log). Auto-stops and downloads when the session ends.
import { useEffect, useRef, useState } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkout } from '@/contexts/WorkoutContext';
import { useSharedStrokeDetection } from '@/contexts/StrokeDetectionContext';
import { createStrokeSensorRecorder, downloadLog, getActiveSensorLog } from '@/lib/strokeSensorLogger';

const startSessionLog = (getLiveRate) => createStrokeSensorRecorder({
  label: 'session',
  durationS: null,
  getLiveRate,
  onDone: (log) => { downloadLog(log); toast.success('Session log saved — attach the file in chat'); },
});

export const SessionLogButton = ({ disabled }) => {
  const { workoutActive } = useWorkout();
  const sd = useSharedStrokeDetection();
  const rateRef = useRef(null);
  rateRef.current = sd?.strokeRate ?? null;
  const [recording, setRecording] = useState(() => !!getActiveSensorLog());

  useEffect(() => {
    const onState = (e) => setRecording(!!e.detail?.recording);
    window.addEventListener('crewz-sensor-log-state', onState);
    return () => window.removeEventListener('crewz-sensor-log-state', onState);
  }, []);

  useEffect(() => {
    if (!workoutActive) getActiveSensorLog()?.stop();
  }, [workoutActive]);

  const toggle = (e) => {
    e.stopPropagation();
    if (disabled) return;
    const rec = getActiveSensorLog();
    if (rec) rec.stop();
    else startSessionLog(() => rateRef.current);
  };

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="session-log-button"
      data-recording={recording ? 'true' : 'false'}
      className={`group relative w-14 h-14 rounded-full transition-transform duration-300 ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:scale-110 active:scale-95'}`}
    >
      <div className={`absolute inset-0 rounded-full border-3 ${recording ? 'border-red-600 bg-red-100' : 'border-zinc-500/70 bg-gradient-to-b from-gray-100 to-gray-200'} shadow-md`} />
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <FileText className={`w-5 h-5 ${recording ? 'text-red-600' : 'text-zinc-600'}`} />
        <span className={`text-[8px] font-bold mt-0.5 ${recording ? 'text-red-600' : 'text-zinc-600'}`}>{recording ? 'LOG ●' : 'LOG'}</span>
      </div>
    </button>
  );
};
